import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";

const FALLBACK = {
  title_accent: "Master tomorrow's skills",
  title_rest: "with Training Basket's job-ready programs.",
  subtitle: "Great universities, industry mentors",
  settings: { speed: 32, grayscale: true },
  items: [],
};

function LogoTile({ item, grayscale }) {
  const initials = item.name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const inner = item.logo ? (
    <img src={item.logo} alt={item.name} className={`h-10 max-w-[160px] object-contain ${grayscale ? "grayscale opacity-70 hover:grayscale-0 hover:opacity-100" : ""} transition-all`} />
  ) : (
    <span className={`flex items-center gap-2.5 ${grayscale ? "opacity-60 hover:opacity-100" : ""} transition-opacity`}>
      <span className="w-9 h-9 rounded-lg bg-slate-900 text-white flex items-center justify-center text-xs font-extrabold shrink-0">{initials}</span>
      <span className="text-sm font-extrabold tracking-tight text-slate-800 whitespace-nowrap uppercase" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{item.name}</span>
    </span>
  );
  return item.link ? (
    <Link to={item.link} className="shrink-0" data-testid={`logo-item-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{inner}</Link>
  ) : (
    <span className="shrink-0" data-testid={`logo-item-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{inner}</span>
  );
}

export default function LogoSlider({ sectionKey = "partner_logos" }) {
  const [section, setSection] = useState(null);

  useEffect(() => {
    api.get(`/website/sections/${sectionKey}`)
      .then((r) => setSection({ ...FALLBACK, ...r.data }))
      .catch(() => setSection(null));
  }, [sectionKey]);

  if (!section) return null;
  const items = (section.items || []).filter((i) => i.visible !== false);
  if (items.length === 0) return null;

  const speed = section.settings?.speed || 32;
  const grayscale = section.settings?.grayscale !== false;
  const track = [...items, ...items];

  return (
    <section className="bg-white border-b border-slate-100" data-testid={`section-${sectionKey}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-14">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-center" data-testid="logo-slider-title">
          <span className="text-rose-600">{section.title_accent}</span>{" "}
          <span className="text-slate-900">{section.title_rest}</span>
        </h2>
        {section.subtitle && (
          <p className="text-center text-slate-500 mt-5 text-sm sm:text-base" data-testid="logo-slider-subtitle">{section.subtitle}</p>
        )}
        <div className="relative mt-8 overflow-hidden" data-testid="logo-slider-track-wrap">
          <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />
          <div className="marquee-track flex items-center gap-14 w-max" style={{ "--marquee-speed": `${speed}s` }} data-testid="logo-slider-track">
            {track.map((item, i) => (
              <LogoTile key={`${item.name}-${i}`} item={item} grayscale={grayscale} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
